import { Period, PeriodType } from '@/common/Graph/types';
import AssetService from '@/lib/Asset';
import { IEXQuote, PolygonAggBar } from '@/lib/Asset/types';
import MarketService from '@/lib/Market';
import ArrayUtils from '@/utils/ArrayUtils';
import dayjs from 'dayjs';
import { GraphDataResponse, Point } from './../../../common/Graph/types';

namespace IndividualAssetGraphUtils {
  export const GRAPH_WIDTH = 760;
  export const GRAPH_HEIGHT = 228;
  export const PERIODS: Period[] = [
    {
      value: PeriodType._1D,
      displayVal: '1D',
      displayText: 'Today',
      dayjsTimestampFormat: 'h:mm A',
      tooltipOffset: -25,
    },
    {
      value: PeriodType._1W,
      displayVal: '1W',
      displayText: 'Past Week',
      dayjsTimestampFormat: 'MMM D, h:mm A',
      tooltipOffset: -45,
    },
    {
      value: PeriodType._1M,
      displayVal: '1M',
      displayText: 'Past Month',
      dayjsTimestampFormat: 'MMM D, YYYY',
      tooltipOffset: -45,
    },
    {
      value: PeriodType._3M,
      displayVal: '3M',
      displayText: 'Past 3 Months',
      dayjsTimestampFormat: 'MMM D, YYYY',
      tooltipOffset: -45,
    },
    {
      value: PeriodType._1A,
      displayVal: '1Y',
      displayText: 'Past Year',
      dayjsTimestampFormat: 'MMM D, YYYY',
      tooltipOffset: -45,
    },
    {
      value: PeriodType.ALL,
      displayVal: 'ALL',
      displayText: 'All Time',
      dayjsTimestampFormat: 'MMM D, YYYY',
      tooltipOffset: -45,
    },
  ];

  async function getLatestPoint(ticker: string): Promise<Point | undefined> {
    const latestQuote = (await AssetService.getQuote(ticker)) as IEXQuote; // fetch latest data regardless of day of the week and market status
    if (!latestQuote) {
      return undefined;
    }

    return {
      x: dayjs(latestQuote.latestUpdate).local().valueOf(),
      y: latestQuote.latestPrice,
    };
  }

  function convertBarsToPoints(bars: PolygonAggBar[]): Point[] {
    return ArrayUtils.orEmptyArray(bars)
      .map((bar, i) => ({
        x: dayjs(bar.t).local().valueOf(),
        y: bar.c,
      }))
      .sort((a, b) => a.x - b.x);
  }

  export async function getPreviousClosePrice(symbol: string, marketOpen: boolean) {
    try {
      if (!marketOpen) {
        // Get close price from previous market day BEFORE last market day
        const closePrice = await AssetService.getPreviousClosePriceBeforeLastMarketDay(symbol);
        return closePrice;
      } else {
        // Get close price from previous market day
        const data = await AssetService.getPreviousMarketDayPrices(symbol);
        return data?.c;
      }
    } catch (e) {
      console.error('error with getting previous close price', e);
    }
  };

  export async function getAggregatePoints(ticker: string, activePeriodType: PeriodType): Promise<GraphDataResponse> {
    let promise: Promise<PolygonAggBar[]>;

    const marketOpen = await MarketService.isMarketOpen();

    const res: GraphDataResponse = {
      points: []
    }

    if (activePeriodType === PeriodType._1D) {
      res.prevClosePrice = await getPreviousClosePrice(ticker, marketOpen);
    }

    try {
      if (activePeriodType === PeriodType._1D) {
        if (marketOpen) {
          promise = AssetService.getIntradayBars(ticker) as Promise<PolygonAggBar[]>;
        } else {
          const lastMarketDay = await MarketService.getLastMarketDate();
          promise = AssetService.getHistoricalBarsByDate(
            ticker,
            dayjs(lastMarketDay).toISOString()
          ) as Promise<PolygonAggBar[]>;
        }
      } else {
        promise = AssetService.getHistoricalBarsByRange(ticker, activePeriodType) as Promise<
          PolygonAggBar[]
        >;
      }
      const bars = await promise;
      res.points = convertBarsToPoints(bars);
      if (marketOpen && res.points.length) {
        const latestPoint = await getLatestPoint(ticker);
        if (latestPoint) {
          res.points.push(latestPoint);
        }
      }

      return res;
    } catch (e) {
      console.error(e);
      return res;
    }
  }
}

export default IndividualAssetGraphUtils;
